import { Document, Page, pdfjs } from 'react-pdf/dist/esm/entry.webpack';
import { useState, useCallback } from 'react';
import { Button, ButtonGroup, Badge } from 'reactstrap';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PdfRenderer = ({ url, height = '80vh', width = '100%' }) => {
    const [numPages, setNumPages] = useState(null);
    const [scale, setScale] = useState(1.2);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const onDocumentLoadSuccess = useCallback(({ numPages }) => {
        setNumPages(numPages);
        setLoading(false);
        setError(null);
    }, []);

    const onDocumentLoadError = useCallback((err) => {
        console.error('PDF load error:', err);
        setError('Failed to load PDF. Please try again.');
        setLoading(false);
    }, []);

    const handleZoomIn = useCallback(() => setScale((s) => s + 0.1), []);
    const handleZoomOut = useCallback(() => setScale((s) => Math.max(s - 0.1, 0.5)), []);

    if (!url) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6B7280' }}>
                Please provide a PDF URL to display.
            </div>
        );
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: height,
                width: width,
                overflow: 'hidden',          // ← prevents blowout
                background: '#F3F4F6',
                borderRadius: 8,
            }}
        >
            {/* ── Toolbar — in-flow, never escapes the container ── */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '8px 16px',
                    background: 'white',
                    borderBottom: '1px solid #E5E7EB',
                    flexShrink: 0,           // ← won't shrink; scrollable area gets the rest
                }}
            >
                <ButtonGroup>
                    <Button
                        color="primary"
                        outline
                        onClick={handleZoomOut}
                        disabled={scale <= 0.5}
                        style={{ fontSize: '0.8rem' }}
                    >
                        <i className="fas fa-search-minus me-1" />
                        Zoom Out
                    </Button>
                    <Button
                        color="primary"
                        outline
                        onClick={handleZoomIn}
                        style={{ fontSize: '0.8rem' }}
                    >
                        <i className="fas fa-search-plus me-1" />
                        Zoom In
                    </Button>
                </ButtonGroup>
                <Badge color="secondary" pill style={{ fontSize: '0.78rem', padding: '4px 10px' }}>
                    {Math.round(scale * 100)}%
                </Badge>
                {numPages && (
                    <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#6B7280' }}>
                        {numPages} page{numPages !== 1 ? 's' : ''}
                    </span>
                )}
            </div>

            {/* ── Scrollable PDF area ── */}
            <div
                style={{
                    flex: 1,
                    overflowY: 'auto',       // ← scroll inside, never outside
                    overflowX: 'auto',
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                {loading && !error && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 40, color: '#3B82F6' }}>
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        Loading PDF…
                    </div>
                )}

                {error && (
                    <div style={{ marginTop: 40, color: '#DC2626', fontSize: '0.9rem' }}>
                        <i className="fas fa-exclamation-triangle me-2" />
                        {error}
                    </div>
                )}

                <Document
                    file={url}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                    {numPages !== null &&
                        Array.from({ length: numPages }, (_, i) => (
                            <Page
                                key={`page_${i + 1}`}
                                pageNumber={i + 1}
                                scale={scale}
                                renderAnnotationLayer
                                renderTextLayer
                                style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', borderRadius: 4 }}
                            />
                        ))}
                </Document>
            </div>
        </div>
    );
};

export { PdfRenderer };